<template>
    <svg
        class="rarity-gem"
        viewBox="0 0 111 111"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        :aria-label="`${rarity} item`">
        <title>{{ rarity }}</title>
        <path d="M40.0833 9.25H21.5833L0 37H30.8333L40.0833 9.25Z" :fill="palette.lightest" />
        <path d="M111 37L89.4167 9.25H70.9167L80.1667 37H111Z" :fill="palette.mid" />
        <path d="M80.1667 37H111L55.5 101.75L80.1667 37Z" :fill="palette.dark" />
        <path d="M30.8333 37H0L55.5 101.75L30.8333 37ZM40.0833 9.25L30.8333 37H80.1667L70.9167 9.25H40.0833Z" :fill="palette.light" />
        <path d="M55.5 101.75L30.8333 37H80.1666L55.5 101.75Z" :fill="palette.mid" />
    </svg>
</template>

<script>
/**
 * The faceted gem from `assets/images/icon/gem.svg`, recoloured per rarity
 * tier. Used for **item rewards** on the season track, so a "Random epic item"
 * is visibly a different prize from a "Random common item" rather than five
 * identical gems down the row.
 *
 * Achievements use `AchievementGem.vue` instead, which renders the illustrated
 * gems from the icon set — the same files the bot uploads as application
 * emojis. That's deliberate rather than an inconsistency: an achievement is a
 * one-off thing you earned and can afford the detail, whereas the track shows
 * fifty rewards in a row where a flat shape at 2rem stays legible and doesn't
 * turn the row into a wall of illustration.
 *
 * Four tones per tier, matching the SVG's original facet structure:
 * a lit top-left, the crown, the body, and the shadowed right edge.
 */
const PALETTES = {
    // `mid` is the tier's base colour from $rarity-* in
    // assets/styles/variables.scss — the same values the shop tags use, so a
    // rare drop here is the same blue as a rare item in the shop. The other
    // three tones are that base lightened and darkened for the facets.
    common: { lightest: '#A8E4C0', light: '#7BD49D', mid: '#52C57E', dark: '#3A9860' },
    rare: { lightest: '#A9C7E4', light: '#7BA8D4', mid: '#5289C5', dark: '#3A6796' },
    epic: { lightest: '#C1BCF4', light: '#A29BEF', mid: '#8379EA', dark: '#5F55C4' },
    legendary: { lightest: '#F7BCAD', light: '#F29B86', mid: '#EE795C', dark: '#C55638' },
    mythical: { lightest: '#F9A8DA', light: '#F67DC7', mid: '#F352B5', dark: '#C63690' },
}

export default {
    name: 'RarityGem',
    props: {
        rarity: {
            type: String,
            default: 'rare',
        },
    },
    computed: {
        palette() {
            // An unknown tier (or a plain `item` reward, which carries no
            // rarity) falls back to the gem's original colours.
            return PALETTES[this.rarity] || PALETTES.rare
        },
    },
}
</script>

<style scoped lang="scss">
.rarity-gem {
    display: block;
}
</style>
